import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AgentLayout.css';

const AgentLayout = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [backgroundTheme, setBackgroundTheme] = useState('none');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Force re-render on location change - FIX for browser back button
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    // Force component to re-render when location changes
    forceUpdate({});
    // Scroll to top on navigation
    window.scrollTo(0, 0);
    // Close mobile menu on navigation
    setIsMobileMenuOpen(false);
    // Close dropdowns on navigation
    setOpenDropdown(null);
  }, [location.pathname]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container') && !event.target.closest('.agent-mobile-menu-item.dropdown-toggle') && !event.target.closest('.agent-mobile-submenu')) {
        setOpenDropdown(null);
      }
      // Close mobile menu when clicking outside
      if (isMobileMenuOpen && !event.target.closest('.agent-mobile-menu') && !event.target.closest('.agent-mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDropdown, isMobileMenuOpen]);
  
  // Background theme definitions
  const backgroundThemes = {
    'none': 'transparent',
    'ocean-gradient': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'sunset-gradient': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'forest-gradient': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'lavender-gradient': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'golden-gradient': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'aurora-gradient': 'linear-gradient(135deg, #3f2b96 0%, #a8c0ff 100%)',
    'geometric-pattern': 'url(data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E)',
    'dots-pattern': 'url(data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%239C92AC" fill-opacity="0.15"%3E%3Ccircle cx="3" cy="3" r="3"/%3E%3Ccircle cx="13" cy="13" r="3"/%3E%3C/g%3E%3C/svg%3E)',
    'waves-pattern': 'url(data:image/svg+xml,%3Csvg width="100" height="20" viewBox="0 0 100 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z" fill="%235C7CFA" fill-opacity="0.1" fill-rule="evenodd"/%3E%3C/svg%3E)'
  };
  
  // Load background theme from localStorage or user profile
  useEffect(() => {
    const savedTheme = localStorage.getItem('agentBackgroundTheme') || user?.backgroundTheme || 'none';
    setBackgroundTheme(savedTheme);
  }, [user]);

  // Listen for theme change events
  useEffect(() => {
    const handleThemeChange = (event) => {
      setBackgroundTheme(event.detail.theme);
    };

    window.addEventListener('backgroundThemeChanged', handleThemeChange);
    return () => {
      window.removeEventListener('backgroundThemeChanged', handleThemeChange);
    };
  }, []);
  
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/agent/profile')) return 'profile';
    if (path.includes('/agent/websites')) return 'websites';
    if (path.includes('/agent/clients') || path.includes('/agent/workflow') || path.includes('/agent/communications') || path.includes('/agent/workflow-analytics')) return 'client-management';
    if (path.includes('/agent/tasks') || path.includes('/agent/task-templates') || path.includes('/agent/workflow-automation')) return 'tasks-projects';
    if (path.includes('/agent/quotes') || path.includes('/agent/itineraries') || path.includes('/agent/commissions') || path.includes('/agent/payment-reminders') || path.includes('/agent/payment-links') || path.includes('/agent/groups') || path.includes('/agent/website')) return 'business-ops';
    if (path.includes('/agent/analytics') || path.includes('/agent/payment-reports')) return 'analytics';
    if (path.includes('/agent/support') || path.includes('/agent/ai-tools') || path.includes('/agent/calendar') || path.includes('/agent/crowd-calendar') || path.includes('/agent/tripshare') || path.includes('/agent/supplier-portals')) return 'tools';
    return 'overview';
  };

  const activeTab = getActiveTab();
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleDropdown = (dropdownId, event) => {
    if (openDropdown === dropdownId) {
      setOpenDropdown(null);
    } else {
      // Only set position for desktop dropdowns that need portal positioning
      if (event && dropdownId.startsWith('desktop-')) {
        const buttonRect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({
          top: buttonRect.bottom + 5,
          left: buttonRect.left
        });
      }
      setOpenDropdown(dropdownId);
    }
  };

  // Helper function to determine if agent should see website menu
  const shouldShowWebsiteMenu = (user) => {
    // Independent agents: always show (included in subscription)
    if (user.role === 'independent_agent') return true;
    
    // Super admins: always show
    if (user.role === 'super_admin') return true;
    
    // Agency admins: always show
    if (user.role === 'agency_admin') return true;
    
    // Agency agents: only if subscribed to website add-on
    if (user.role === 'agent' && (user.agency_id || user.agencyId)) {
      return user.website_subscription_active === 1;
    }
    
    return false;
  };

  // Helper function to check if user is commission-only agent
  const isCommissionOnlyAgent = (user) => {
    return user && user.role === 'agent' && user.status === 'commission_only';
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', path: '/agent/dashboard' },
    { 
      id: 'client-management', 
      label: '📊 Client Management', 
      hasDropdown: true,
      children: [
        { id: 'clients', label: 'Clients', path: '/agent/clients' },
        { id: 'workflow', label: 'Workflow Stages', path: '/agent/workflow' },
        { id: 'communications', label: 'Communications', path: '/agent/communications' },
        { id: 'workflow-analytics', label: 'Workflow Analytics', path: '/agent/workflow-analytics' }
      ]
    },
    { 
      id: 'tasks-projects', 
      label: '📋 Tasks & Projects', 
      hasDropdown: true,
      children: [
        { id: 'tasks', label: 'Tasks', path: '/agent/tasks' },
        ...(user.role === 'agency_admin' || user.role === 'super_admin' ? 
          [
            { id: 'task-templates', label: 'Task Templates', path: '/agent/task-templates' },
            { id: 'workflow-automation', label: 'Workflow Automation', path: '/agent/workflow-automation' }
          ] : 
          []
        )
      ]
    },
    { 
      id: 'business-ops', 
      label: '💰 Business Operations', 
      hasDropdown: true,
      children: [
        { id: 'leads', label: 'Leads', path: '/agent/leads' },
        { id: 'quotes', label: 'Quotes', path: '/agent/quotes' },
        { id: 'itineraries', label: 'Itineraries', path: '/agent/itineraries' },
        { id: 'commissions', label: 'Commissions', path: '/agent/commissions' },
        { id: 'payment-reminders', label: 'Payment Reminders', path: '/agent/payment-reminders' },
        { id: 'payment-links', label: 'Payment Links', path: '/agent/payment-links' },
        { id: 'groups', label: 'Groups', path: '/agent/groups' },
        { id: 'activeagent-ai', label: 'Voyager AI', path: '/agent/activeagent-ai' }
        // { id: 'website', label: 'Website Management', path: '/agent/website' }
      ]
    },
    { 
      id: 'analytics', 
      label: '📈 Analytics & Reports', 
      hasDropdown: true,
      children: [
        { id: 'analytics', label: 'Analytics Dashboard', path: '/agent/analytics' },
        { id: 'payment-reports', label: 'Payment Reports', path: '/agent/payment-reports' }
      ]
    },
    // Website menu moved to Business Operations submenu
    { 
      id: 'tools', 
      label: '🔧 Tools', 
      hasDropdown: true,
      children: [
        { id: 'calendar', label: '📅 Calendar', path: '/agent/calendar' },
        { id: 'supplier-portals', label: '🔗 Supplier Portals', path: '/agent/supplier-portals' },
        { id: 'tripshare', label: '🌍 TripShare', path: '/agent/tripshare' },
        { id: 'crowd-calendar', label: '🎢 Theme Park Crowds', path: '/agent/crowd-calendar' },
        { id: 'ai-tools', label: '🤖 AI Tools', path: '/agent/ai-tools' },
        { id: 'support', label: '🎧 Support', path: '/agent/support' }
      ]
    },
    { id: 'profile', label: 'My Profile', path: '/agent/profile' }
  ];

  // Filter menu items based on user status
  const getFilteredMenuItems = () => {
    // Commission-only agents only see Profile
    if (isCommissionOnlyAgent(user)) {
      return [
        { id: 'profile', label: 'My Profile', path: '/agent/profile' }
      ];
    }
    
    // Regular agents see full menu
    return menuItems;
  };

  const navigationItems = getFilteredMenuItems();

  return (
    <div className="agent-layout-wrapper">
      {/* Background decoration - only visible on larger screens */}
      <div 
        className="agent-background-decoration"
        style={{
          background: backgroundThemes[backgroundTheme] || 'transparent',
          backgroundSize: backgroundTheme.includes('pattern') ? '20px 20px' : 'cover'
        }}
      />
      
      {/* Header with calming blue gradient - moved outside layout-content for full width */}
      <header className="agent-header">
        <div className="agent-header-content">
          <div className="agent-header-inner">
            <div className="agent-header-brand">
              <h1 className="agent-header-title">
                Travel+ CRM
              </h1>
              <span className="agent-header-subtitle">
                Agent Dashboard
              </span>
            </div>
            
            {/* Conditionally render mobile button only on mobile */}
            {isMobile && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="agent-mobile-menu-btn"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            )}
            
            {/* Desktop user info */}
            <div className="agent-header-user">
              <span className="agent-header-welcome">
                Welcome, {(() => {
                  const result = loading ? '...' : user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.name || user?.email || 'Agent';
                  console.log('🔍 Welcome message debug:', { loading, user, result });
                  return result;
                })()}
              </span>
              <button
                onClick={() => navigate('/agent/profile')}
                className="agent-header-profile"
                title="My Profile"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
              <button
                onClick={handleLogout}
                className="agent-header-logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="agent-mobile-menu">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                  <div key={item.id}>
                    {item.hasDropdown ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Mobile dropdown toggle:', item.id);
                            toggleDropdown(`mobile-${item.id}`, e);
                          }}
                          className={`agent-mobile-menu-item ${activeTab === item.id ? 'active' : ''} dropdown-toggle`}
                          style={{ touchAction: 'manipulation' }}
                        >
                          {item.label}
                          <svg 
                            className={`dropdown-arrow ${openDropdown === `mobile-${item.id}` ? 'open' : ''}`} 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openDropdown === `mobile-${item.id}` && (
                          <div className="agent-mobile-submenu">
                            {item.children.map((child) => (
                              <div
                                key={child.id}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsMobileMenuOpen(false);
                                  setOpenDropdown(null);
                                  // Navigate to the selected page
                                  const fullUrl = window.location.origin + child.path;
                                  window.location.href = fullUrl;
                                }}
                                className="agent-mobile-submenu-item"
                                style={{ 
                                  touchAction: 'manipulation',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  WebkitTapHighlightColor: 'transparent'
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(child.path);
                                    setIsMobileMenuOpen(false);
                                    setOpenDropdown(null);
                                  }
                                }}
                              >
                                {child.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                          // Navigate to the selected page
                          const fullUrl = window.location.origin + item.path;
                          window.location.href = fullUrl;
                        }}
                        className={`agent-mobile-menu-item ${activeTab === item.id ? 'active' : ''}`}
                        style={{ touchAction: 'manipulation' }}
                      >
                        {item.label}
                      </button>
                    )}
                  </div>
                ))}
                <div className="agent-mobile-menu-divider">
                  <div className="agent-mobile-menu-user">
                    {user?.name || user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || 'Agent'}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="agent-mobile-menu-item"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>

        <div className="agent-layout-content">
          {/* Site-wide Announcement Banner */}
          {!localStorage.getItem('email-fix-banner-dismissed-may14') && (
            <div className="site-announcement-banner">
              <div className="announcement-content">
                <div className="announcement-icon">
                  ✅
              </div>
              <div className="announcement-text">
                We were aware of an issue with email delivery earlier today and have now resolved it. All pending emails have been sent successfully. Email monitoring is now active to prevent future issues.
              </div>
              <button 
                className="announcement-close"
                onClick={() => {
                  document.querySelector('.site-announcement-banner').style.display = 'none';
                  localStorage.setItem('email-fix-banner-dismissed-may14', 'true');
                }}
                title="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="agent-nav-tabs">
          <div className="agent-nav-tabs-inner">
            <div className="agent-nav-tabs-list">
              {navigationItems.map((item) => (
                <div key={item.id} className="nav-item-container">
                  {item.hasDropdown ? (
                    <div className="dropdown-container">
                      <button
                        onClick={(e) => toggleDropdown(`desktop-${item.id}`, e)}
                        className={`agent-nav-tab ${activeTab === item.id ? 'active' : ''} dropdown-toggle`}
                      >
                        {item.label}
                        <svg 
                          className={`dropdown-arrow ${openDropdown === `desktop-${item.id}` ? 'open' : ''}`} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {openDropdown === `desktop-${item.id}` && ReactDOM.createPortal(
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          style={{
                            position: 'fixed',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '16px',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                            zIndex: 999999,
                            minWidth: '200px',
                            overflow: 'visible',
                            marginTop: '0.5rem',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'auto'
                          }}
                        >
                          {item.children?.map((child) => (
                            <div
                              key={child.id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenDropdown(null);
                                // Navigate to the selected page
                                const fullUrl = window.location.origin + child.path;
                                window.location.href = fullUrl;
                              }}
                              className="dropdown-menu-item"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '1rem 1.5rem',
                                border: 'none',
                                background: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                color: '#374151',
                                transition: 'background-color 0.2s ease',
                                userSelect: 'none',
                                pointerEvents: 'auto'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f3f4f6';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              {child.label}
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate(item.path)}
                      className={`agent-nav-tab ${activeTab === item.id ? 'active' : ''}`}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Render child routes with key to force re-render */}
        <main className="agent-main-content">
          <div className="agent-main-inner">
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AgentLayout;
